import React, { useCallback, useEffect, useState } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import { API_URL } from "../config";

// Photos of one album: paged grid, grouped by upload day, with a lightbox.
// Shared by the gallery (when there is a single album) and the album page.
const PhotoGrid = ({ album, groupByDate = true }) => {
  const [photos, setPhotos] = useState([]);
  const [nextPageToken, setNextPageToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const fetchPhotos = useCallback(async (albumId, pageToken = "") => {
    try {
      const response = await fetch(
        `${API_URL}/photos?albumId=${albumId}&pageToken=${pageToken}`
      );
      if (!response.ok) throw new Error("Request failed");

      const data = await response.json();
      setPhotos((previous) => [...previous, ...(data.mediaItems || [])]);
      setNextPageToken(data.nextPageToken || "");
      setError(null);
    } catch (err) {
      setError("Fotky se nepodařilo načíst");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Reset when switching album, otherwise photos would accumulate.
    setPhotos([]);
    setNextPageToken("");
    setLoading(true);
    fetchPhotos(album.id);
  }, [album.id, fetchPhotos]);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader" />
      </div>
    );
  }

  if (error) return <div className="article-error">{error}</div>;

  if (!photos.length) {
    return <div className="article-error">Zatím zde nejsou žádné fotky.</div>;
  }

  const groups = groupByDate
    ? [
        ...photos
          .reduce((map, photo) => {
            const date = photo.createdTime
              ? new Date(photo.createdTime)
              : null;
            const key = date
              ? `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
              : "";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(photo);
            return map;
          }, new Map())
          .entries(),
      ]
    : [["", photos]];

  const thumbnail = (photo) => {
    const index = photos.findIndex((item) => item.id === photo.id);

    return (
      <div key={photo.id} className="grid-item">
        <img
          className="album-photo"
          src={photo.thumbnailUrl}
          loading="lazy"
          alt={photo.name || "Fotka z galerie"}
          width="200"
          style={{ cursor: "pointer" }}
          onClick={() => setLightboxIndex(index)}
        />
      </div>
    );
  };

  return (
    <div className="gallery-container">
      {groups.map(([date, group]) => (
        <div key={date || "all"} style={{ marginBottom: "2rem" }}>
          {date && <p className="date-photos">{`Nahráno ${date}`}</p>}
          <div className="grid">{group.map(thumbnail)}</div>
        </div>
      ))}

      {nextPageToken && (
        <div className="load-more-container">
          <button
            type="button"
            className="load-more-button"
            onClick={() => fetchPhotos(album.id, nextPageToken)}
          >
            Načíst další
          </button>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          mainSrc={photos[lightboxIndex].previewUrl}
          nextSrc={photos[(lightboxIndex + 1) % photos.length].previewUrl}
          prevSrc={
            photos[(lightboxIndex + photos.length - 1) % photos.length]
              .previewUrl
          }
          onCloseRequest={() => setLightboxIndex(null)}
          onMovePrevRequest={() =>
            setLightboxIndex((lightboxIndex + photos.length - 1) % photos.length)
          }
          onMoveNextRequest={() =>
            setLightboxIndex((lightboxIndex + 1) % photos.length)
          }
        />
      )}
    </div>
  );
};

export default PhotoGrid;
